// ============================================================
//  LiveChatInbox.js — Website Widget → Chats
//  Answer website visitors in real time (polling, like the rest of the app).
// ============================================================
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import toast from 'react-hot-toast';
import { getLiveChats, getLiveChat, liveChatAction, sendLiveChatImage, widgetFileUrl } from '../../api/client';
import ResolveChatSheet from './ResolveChatSheet';
import './LiveChatResolve.css';

const UNDO_MS = 5000;
function Stars({ n }) {
  return (
    <span className="lcr-stars" aria-label={`${n} out of 5 stars`}>
      {[1, 2, 3, 4, 5].map(i => <i key={i} className={`fa-solid fa-star ${i <= n ? '' : 'is-off'}`} />)}
    </span>
  );
}

const PALETTE = ['#00A676', '#2563EB', '#7C3AED', '#D97706', '#DB2777', '#0891B2', '#65A30D', '#DC2626'];
function colorFor(s) {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = s.charCodeAt(i) + ((h << 5) - h);
  return PALETTE[Math.abs(h) % PALETTE.length];
}
function displayName(c) { return c.name || (c.email ? c.email.split('@')[0] : '') || (c.phone ? `+${c.phone}` : '') || `Visitor #${c.id}`; }
function toDate(s) { return s ? new Date(String(s).replace(' ', 'T')) : null; }
function ago(s) {
  const d = toDate(s); if (!d || Number.isNaN(d.getTime())) return '';
  const m = Math.max(0, Math.round((Date.now() - d.getTime()) / 60000));
  if (m < 1) return 'now';
  if (m < 60) return `${m}m`;
  if (m < 1440) return `${Math.round(m / 60)}h`;
  return d.toLocaleDateString([], { day: 'numeric', month: 'short' });
}
function clock(ms) { return new Date(ms).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }); }
function dayLabel(ms) {
  const d = new Date(ms); const t = new Date();
  const y = new Date(); y.setDate(t.getDate() - 1);
  if (d.toDateString() === t.toDateString()) return 'Today';
  if (d.toDateString() === y.toDateString()) return 'Yesterday';
  return d.toLocaleDateString([], { weekday: 'short', day: 'numeric', month: 'short' });
}
function device(ua = '') {
  const os = /iPhone|iPad/.test(ua) ? 'iPhone' : /Android/.test(ua) ? 'Android' : /Windows/.test(ua) ? 'Windows' : /Mac OS X/.test(ua) ? 'Mac' : /Linux/.test(ua) ? 'Linux' : '';
  const br = /Edg\//.test(ua) ? 'Edge' : /OPR\//.test(ua) ? 'Opera' : /Chrome\//.test(ua) ? 'Chrome' : /Safari\//.test(ua) ? 'Safari' : /Firefox\//.test(ua) ? 'Firefox' : '';
  return [br, os].filter(Boolean).join(' on ') || 'Unknown device';
}
function shortUrl(u = '') { try { const x = new URL(u); return (x.host + x.pathname).replace(/\/$/, ''); } catch { return u; } }
// Shrink big photos in the browser first (max 2000 px) — the server re-checks and re-encodes anyway.
function shrink(file) {
  return new Promise(resolve => {
    if (!/^image\/(png|jpeg|webp)$/.test(file.type) || file.size < 1.5 * 1024 * 1024) { resolve(file); return; }
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const sc = Math.min(1, 2000 / Math.max(img.naturalWidth, img.naturalHeight));
      const c = document.createElement('canvas');
      c.width = Math.round(img.naturalWidth * sc); c.height = Math.round(img.naturalHeight * sc);
      c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
      URL.revokeObjectURL(url);
      c.toBlob(b => resolve(b && b.size < file.size ? b : file), 'image/jpeg', 0.85);
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
    img.src = url;
  });
}
function sync(d) { window.dispatchEvent(new CustomEvent('ck:lc-sync', { detail: { unreadChats: d.unreadChats, lastIn: d.lastIn, latest: d.latest } })); }

export default function LiveChatInbox({ widgets, presence, initialChat = null, focusReply = false }) {
  const [status, setStatus]   = useState('open');
  const [widgetId, setWidget] = useState(0);
  const [q, setQ]             = useState('');
  const [convs, setConvs]     = useState(null);
  const [selId, setSelId]     = useState(initialChat);
  const [conv, setConv]       = useState(null);
  const [msgs, setMsgs]       = useState([]);
  const [draft, setDraft]     = useState('');
  const [sending, setSending] = useState(false);
  const [ask, setAsk]         = useState(null);   // 'delete' | 'block'
  const [uploading, setUploading] = useState(false);
  const [resolving, setResolving] = useState(false);     // the "Resolve this chat?" sheet is open
  const [pending, setPending]     = useState(null);      // { id, message, rating, until } — undo window
  const [freshDone, setFreshDone] = useState(0);         // chat id that was just resolved (plays the check animation)
  const fileRef   = useRef(null);
  const replyRef  = useRef(null);
  const focusNext = useRef(!!focusReply);
  const pendingRef = useRef(null);
  const undoTimer = useRef(null);
  const selIdRef  = useRef(selId);
  selIdRef.current = selId;
  const lastIdRef = useRef(0);
  const bodyRef   = useRef(null);
  const typingAt  = useRef(0);
  const stickRef  = useRef(true);
  const [isMobile, setIsMobile] = useState(() => window.matchMedia('(max-width: 860px)').matches);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 860px)');
    const on = () => setIsMobile(mq.matches);
    mq.addEventListener ? mq.addEventListener('change', on) : mq.addListener(on);
    return () => { mq.removeEventListener ? mq.removeEventListener('change', on) : mq.removeListener(on); };
  }, []);

  // Phone: an open chat covers the screen, and the phone's Back button returns to the list.
  useEffect(() => {
    if (!isMobile || !selId) return undefined;
    window.history.pushState({ lcChat: selId }, '');
    const onPop = () => setSelId(null);
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [isMobile, selId]);
  function closeThread() {
    if (isMobile && window.history.state?.lcChat) window.history.back();
    else setSelId(null);
  }

  // ── conversation list (every 4 s) ──
  const loadList = useCallback(async () => {
    try {
      const res = await getLiveChats({ status, widget: widgetId || undefined, q: q.trim() || undefined });
      setConvs(res.data.conversations || []);
      sync(res.data);
    } catch { /* keep the last list; try again on the next tick */ }
  }, [status, widgetId, q]);
  useEffect(() => {
    loadList();
    const t = setInterval(() => { if (!document.hidden) loadList(); }, 4000);
    return () => clearInterval(t);
  }, [loadList]);

  // A desktop notification was clicked → open that chat
  useEffect(() => {
    const open = e => {
      if (!e.detail?.chatId) return;
      if (e.detail.reply) focusNext.current = true;
      setStatus('open'); setSelId(e.detail.chatId);
      if (e.detail.reply) setTimeout(() => replyRef.current?.focus(), 400);
    };
    window.addEventListener('ck:lc-open', open);
    return () => window.removeEventListener('ck:lc-open', open);
  }, []);

  // ── open a conversation ──
  useEffect(() => {
    if (!selId) { setConv(null); setMsgs([]); return undefined; }
    let alive = true;
    lastIdRef.current = 0;
    stickRef.current = true;
    setMsgs([]); setConv(null); setAsk(null);
    const pull = async (first) => {
      try {
        const res = await getLiveChat(selId, first ? 0 : lastIdRef.current, !document.hidden);
        if (!alive) return;
        setConv(res.data.conversation);
        const fresh = res.data.messages || [];
        if (first) setMsgs(fresh);
        else if (fresh.length) setMsgs(m => [...m, ...fresh.filter(x => x.id > (m[m.length - 1]?.id || 0))]);
        if (fresh.length) lastIdRef.current = fresh[fresh.length - 1].id;
        if (first || fresh.length) setConvs(list => list && list.map(c => (c.id === selId ? { ...c, unread: 0 } : c)));
      } catch (e) {
        if (e?.response?.status === 404 && alive) { setSelId(null); toast.error('That chat was deleted'); }
      }
    };
    pull(true);
    const t = setInterval(() => { if (!document.hidden) pull(false); }, 3000);
    return () => { alive = false; clearInterval(t); };
  }, [selId]);

  // keep the newest message in view unless the agent scrolled up to read
  useEffect(() => {
    const el = bodyRef.current;
    if (el && stickRef.current) el.scrollTop = el.scrollHeight;
  }, [msgs, conv?.typing]);
  // Opened from a notification's "Reply" → put the cursor in the reply box
  useEffect(() => {
    if (conv && focusNext.current) { focusNext.current = false; setTimeout(() => replyRef.current?.focus(), 60); }
  }, [conv]);

  // ── resolve (with a 5-second undo) ──
  const commitClose = useCallback(async () => {
    const p = pendingRef.current;
    if (!p) return;
    pendingRef.current = null;
    clearTimeout(undoTimer.current);
    setPending(null);
    try {
      const res = await liveChatAction('close', p.id, { message: p.message, rating: p.rating });
      if (selIdRef.current === p.id) {
        setConv(res.data.conversation);
        setMsgs(res.data.messages || []);
        lastIdRef.current = res.data.messages?.length ? res.data.messages[res.data.messages.length - 1].id : 0;
        stickRef.current = true;
        setFreshDone(p.id);
        setTimeout(() => setFreshDone(d => (d === p.id ? 0 : d)), 2500);
      }
      toast.success(p.rating ? 'Chat resolved — rating requested' : 'Chat resolved', { icon: '✅' });
      loadList();
    } catch (err) { toast.error(err?.response?.data?.message || 'Could not resolve the chat'); }
  }, [loadList]);
  const commitRef = useRef(commitClose);
  commitRef.current = commitClose;

  function startResolve({ message, rating }) {
    setResolving(false);
    const p = { id: selId, message, rating, until: Date.now() + UNDO_MS };
    pendingRef.current = p;
    setPending(p);
    clearTimeout(undoTimer.current);
    undoTimer.current = setTimeout(() => commitRef.current(), UNDO_MS);
  }
  function undoResolve() {
    clearTimeout(undoTimer.current);
    pendingRef.current = null;
    setPending(null);
    toast('Chat kept open', { icon: '↩️' });
    setTimeout(() => replyRef.current?.focus(), 50);
  }
  // Switching chats, leaving the page or closing the app sends a pending resolve straight away.
  useEffect(() => () => { if (pendingRef.current) commitRef.current(); }, [selId]);
  useEffect(() => {
    const flush = () => { if (pendingRef.current) commitRef.current(); };
    window.addEventListener('pagehide', flush);
    return () => { window.removeEventListener('pagehide', flush); flush(); };
  }, []);

  function onScroll() {
    const el = bodyRef.current;
    if (el) stickRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  }

  async function send(e) {
    e?.preventDefault();
    const text = draft.trim();
    if (!text || sending || !selId) return;
    setSending(true);
    try {
      const res = await liveChatAction('reply', selId, { text, after: lastIdRef.current });
      const fresh = res.data.messages || [];
      setMsgs(m => [...m, ...fresh.filter(x => x.id > (m[m.length - 1]?.id || 0))]);
      if (fresh.length) lastIdRef.current = fresh[fresh.length - 1].id;
      setConv(res.data.conversation);
      setDraft('');
      stickRef.current = true;
      loadList();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Reply not sent');
    }
    setSending(false);
  }

  async function sendImage(file) {
    if (!file || !selId || uploading) return;
    if (!/^image\/(png|jpeg|webp|gif)$/.test(file.type)) { toast.error('Only photos can be sent (JPG, PNG, WebP or GIF)'); return; }
    setUploading(true);
    try {
      const blob = await shrink(file);
      if (blob.size > 8 * 1024 * 1024) throw new Error('That photo is too big (max 8 MB)');
      const caption = draft.trim();
      const res = await sendLiveChatImage(selId, new File([blob], blob.type === 'image/png' ? 'screenshot.png' : 'photo.jpg', { type: blob.type }), caption, lastIdRef.current);
      const fresh = res.data.messages || [];
      setMsgs(m => [...m, ...fresh.filter(x => x.id > (m[m.length - 1]?.id || 0))]);
      if (fresh.length) lastIdRef.current = fresh[fresh.length - 1].id;
      setConv(res.data.conversation);
      if (caption) setDraft('');
      stickRef.current = true;
      loadList();
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || 'Photo not sent');
    }
    setUploading(false);
  }
  function onPaste(e) {
    const items = e.clipboardData?.items || [];
    for (const it of items) {
      if (it.kind === 'file' && it.type.startsWith('image/')) { e.preventDefault(); sendImage(it.getAsFile()); return; }
    }
  }
  function onDrop(e) {
    const f = e.dataTransfer?.files?.[0];
    if (f && f.type.startsWith('image/')) { e.preventDefault(); sendImage(f); }
  }

  function onDraft(v) {
    setDraft(v);
    if (selId && v.trim() && Date.now() - typingAt.current > 3000) {
      typingAt.current = Date.now();
      liveChatAction('typing', selId).catch(() => {});
    }
  }

  async function act(action) {
    if ((action === 'delete_chat' && ask !== 'delete') || (action === 'block' && ask !== 'block')) {
      setAsk(action === 'block' ? 'block' : 'delete');
      return;
    }
    setAsk(null);
    try {
      const res = await liveChatAction(action, selId);
      if (action === 'delete_chat') { setSelId(null); toast.success('Chat deleted'); }
      else {
        setConv(res.data.conversation);
        setMsgs(res.data.messages || []);
        lastIdRef.current = res.data.messages?.length ? res.data.messages[res.data.messages.length - 1].id : 0;
        toast.success(res.data.message);
      }
      loadList();
    } catch (err) { toast.error(err?.response?.data?.message || 'Could not update the chat'); }
  }

  const widgetCount = widgets.length;
  const grouped = useMemo(() => {
    const out = []; let day = '';
    msgs.forEach(m => {
      const d = dayLabel(m.at);
      if (d !== day) { out.push({ k: `d${m.id}`, day: d }); day = d; }
      out.push({ k: m.id, m });
    });
    return out;
  }, [msgs]);

  const list = convs || [];
  const thread = (
  <section className="lci-thread" aria-label="Conversation">
    {!selId ? (
      <div className="lci-placeholder">
        <span><i className="fa-regular fa-comment-dots" /></span>
        <b>Pick a chat to reply</b>
        <p>New messages make a sound and show a badge on Website Widget in the menu.</p>
      </div>
    ) : !conv ? (
      <div className="lci-placeholder"><span className="wwg-spin" /></div>
    ) : (
      <>
        <header className="lci-head">
          <button type="button" className="lci-back" onClick={closeThread} aria-label="Back to chats"><i className="fa-solid fa-arrow-left" /></button>
          <span className="lci-av" style={{ background: colorFor(displayName(conv)) }}>
            {displayName(conv)[0]?.toUpperCase()}
            {conv.onPage && <i className="lci-live" />}
          </span>
          <div className="lci-who">
            <b>{displayName(conv)}</b>
            <span>{conv.typing ? <em className="is-typing">typing…</em> : conv.onPage ? 'On your website now' : `Last seen ${ago(conv.lastSeenAt)} ago`.replace('now ago', 'just now')}</span>
          </div>
          <div className="lci-actions">
            {conv.aiOn && conv.status === 'open' && (conv.aiPaused
              ? <button type="button" className="wwg-btn wwg-btn-ghost wwg-btn-sm lci-ai-btn" onClick={() => act('ai_resume')} title="Let the AI answer this chat again"><span className="aia-badge">AI</span> <span>Let AI answer</span></button>
              : <button type="button" className="wwg-btn wwg-btn-ghost wwg-btn-sm lci-ai-btn" onClick={() => act('ai_pause')} title="Stop the AI in this chat"><i className="fa-solid fa-pause" /> <span>Pause AI</span></button>)}
            {conv.status === 'open'
              ? <button type="button" className="wwg-btn wwg-btn-ghost wwg-btn-sm lcr-head-btn" onClick={() => setResolving(true)} disabled={pending?.id === selId}><i className="fa-solid fa-check" /> <span>Resolve</span></button>
              : <button type="button" className="wwg-btn wwg-btn-ghost wwg-btn-sm" onClick={() => act('reopen')}><i className="fa-solid fa-rotate-left" /> <span>Reopen</span></button>}
          </div>
        </header>

        <div className="lci-info">
          {conv.phone && (
            <>
              <a href={`https://wa.me/${conv.phone}`} target="_blank" rel="noopener noreferrer"><i className="fa-brands fa-whatsapp" /> +{conv.phone}</a>
              <a href={`tel:+${conv.phone}`}><i className="fa-solid fa-phone" /> Call</a>
            </>
          )}
          {conv.email && <a href={`mailto:${conv.email}`}><i className="fa-regular fa-envelope" /> {conv.email}</a>}
          {conv.page && <a href={conv.page} target="_blank" rel="noopener noreferrer" title={conv.page}><i className="fa-regular fa-file-lines" /> {shortUrl(conv.page)}</a>}
          <span><i className="fa-solid fa-display" /> {device(conv.userAgent)}</span>
          {widgetCount > 1 && <span><i className="fa-solid fa-globe" /> {conv.widget}</span>}
          {conv.referrer && <span title={conv.referrer}><i className="fa-solid fa-arrow-right-to-bracket" /> from {shortUrl(conv.referrer).split('/')[0]}</span>}
        </div>

        {conv.summary && (conv.needsHuman || conv.aiPaused) && (
          <div className={`lci-summary ${conv.needsHuman ? 'is-need' : ''}`}>
            <i className="fa-solid fa-wand-magic-sparkles" />
            <div><b>{conv.needsHuman ? 'Handed to you by the AI' : 'AI summary'}</b><span>{conv.summary}</span></div>
          </div>
        )}
        <div className="lci-body" ref={bodyRef} onScroll={onScroll} role="log" aria-live="polite" onDragOver={e => e.preventDefault()} onDrop={onDrop}>
          {grouped.map(({ k, day, m }) => (day
            ? <div key={k} className="lci-day">{day}</div>
            : m.type === 'rated'
              ? (
                <div key={k} className="lcr-rated">
                  <Stars n={m.score} />
                  <b>{displayName(conv)} rated this chat {m.score}/5</b>
                  {m.text && <q>{m.text}</q>}
                </div>
              )
            : m.type === 'rating'
              ? <div key={k} className="lcr-asked"><i className="fa-solid fa-star" /> Rating requested</div>
            : m.dir === 'sys' && /was closed/.test(m.text)
              ? <div key={k} className="lcr-divider"><span><i className="fa-solid fa-circle-check" /> Resolved · {clock(m.at)}</span></div>
            : m.dir === 'sys'
              ? <div key={k} className="lci-sys">{m.text}</div>
              : (
                <div key={k} className={`lci-msg ${m.dir === 'out' ? 'is-out' : 'is-in'} ${m.ai ? 'is-ai' : ''}`}>
                  {m.type === 'image' && m.file && (
                    <a className="lci-pic" href={widgetFileUrl(m.file)} target="_blank" rel="noopener noreferrer" aria-label="Open photo">
                      <img src={widgetFileUrl(m.file)} alt={m.text || 'Photo'} style={m.w && m.h ? { aspectRatio: `${m.w} / ${m.h}` } : undefined}
                        onLoad={() => { const el = bodyRef.current; if (el && stickRef.current) el.scrollTop = el.scrollHeight; }} />
                    </a>
                  )}
                  {(m.type !== 'image' || m.text) && <div className="lci-bubble">{m.text}</div>}
                  <span>{m.ai && <span className="aia-badge">AI</span>} {m.dir === 'out' && m.sender ? `${m.sender} · ` : ''}{clock(m.at)}</span>
                </div>
              )))}
          {conv.typing && <div className="lci-typing" aria-label="Visitor is typing"><i /><i /><i /></div>}
        </div>

        {pending?.id === selId ? (
          <div className="lcr-undo" role="status">
            <i className="fa-solid fa-circle-check" />
            <span>
              <b>Resolving this chat…</b>
              <em>{[pending.message && 'goodbye message', pending.rating && 'rating card'].filter(Boolean).join(' + ') || 'Nothing is sent to the visitor'}{pending.message || pending.rating ? ' will be sent' : ''}</em>
            </span>
            <button type="button" onClick={undoResolve}>Undo</button>
            <i className="lcr-undo-bar" style={{ animationDuration: `${UNDO_MS}ms` }} aria-hidden="true" />
          </div>
        ) : conv.status === 'closed' && !conv.blocked && (
          <div className={`lcr-closed ${freshDone === conv.id ? 'is-new' : ''}`}>
            <span className="lcr-badge"><i className="fa-solid fa-check" /></span>
            <div className="lcr-closed-txt">
              <b>Resolved{conv.closedAt ? ` ${ago(conv.closedAt) === 'now' ? 'just now' : `${ago(conv.closedAt)} ago`}` : ''}</b>
              <span>
                {conv.rating > 0
                  ? <><Stars n={conv.rating} /> {conv.rating}/5{conv.ratingNote && <q>{conv.ratingNote}</q>}</>
                  : msgs.some(m => m.type === 'rating' && !m.score)
                    ? <span className="lcr-wait"><i className="fa-solid fa-star" /> Waiting for their rating…</span>
                    : 'The visitor can’t reply to a resolved chat — they can start a new one.'}
              </span>
            </div>
            <button type="button" className="wwg-btn wwg-btn-ghost wwg-btn-sm" onClick={() => act('reopen')}><i className="fa-solid fa-rotate-left" /> <span>Reopen</span></button>
          </div>
        )}

        {conv.status === 'closed' && !conv.blocked ? null : conv.blocked ? (
          <div className="lci-blocked">
            <span><i className="fa-solid fa-ban" /> You blocked this visitor. Their new messages are not delivered.</span>
            <button type="button" className="wwg-btn wwg-btn-ghost wwg-btn-sm" onClick={() => act('unblock')}>Unblock</button>
          </div>
        ) : (
          <form className="lci-compose" onSubmit={send}>
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" hidden
              onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; if (f) sendImage(f); }} />
            <button type="button" className="lci-attach" onClick={() => fileRef.current?.click()} disabled={uploading}
              aria-label="Send a photo or screenshot" title="Send a photo — or paste a screenshot into the box">
              {uploading ? <span className="wwg-spin" /> : <i className="fa-regular fa-image" />}
            </button>
            <textarea
              ref={replyRef}
              onPaste={onPaste}
              value={draft}
              onChange={e => onDraft(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder={`Reply to ${displayName(conv)}…`}
              rows={1}
              maxLength={4000}
              aria-label="Reply"
            />
            <button type="submit" className="wwg-btn wwg-btn-brand" disabled={!draft.trim() || sending} aria-label="Send reply">
              {sending ? <span className="wwg-spin" /> : <i className="fa-solid fa-paper-plane" />}
            </button>
          </form>
        )}

        <div className="lci-more">
          <span>{conv.replyAs ? <>Visitors see your replies as <b>{conv.replyAs}</b> · </> : null}Shift + Enter for a new line · paste a screenshot to send it</span>
          <div>
            {!conv.blocked && (
              <button type="button" onClick={() => act('block')} onBlur={() => setAsk(a => (a === 'block' ? null : a))}>
                {ask === 'block' ? 'Tap again to block' : 'Block visitor'}
              </button>
            )}
            <button type="button" className="is-bad" onClick={() => act('delete_chat')} onBlur={() => setAsk(a => (a === 'delete' ? null : a))}>
              {ask === 'delete' ? 'Tap again to delete' : 'Delete chat'}
            </button>
          </div>
        </div>
        {resolving && (
          <ResolveChatSheet
            name={displayName(conv)}
            replyAs={conv.replyAs}
            blocked={conv.blocked}
            startedAt={conv.createdAt}
            msgCount={msgs.filter(m => m.dir !== 'sys').length}
            onCancel={() => setResolving(false)}
            onConfirm={startResolve}
          />
        )}
      </>
    )}
  </section>
  );

  return (
    <div className={`lci ${selId ? 'has-sel' : ''}`}>
      {/* ── left: conversations ── */}
      <aside className="lci-list" aria-label="Chats">
        <div className="lci-tools">
          <div className="lci-seg" role="tablist" aria-label="Show">
            {[['human', 'Needs you'], ['open', 'Open'], ['closed', 'Resolved'], ['all', 'All']].map(([v, l]) => (
              <button key={v} type="button" role="tab" aria-selected={status === v} className={status === v ? 'is-on' : ''} onClick={() => setStatus(v)}>{l}</button>
            ))}
          </div>
          <div className="lci-search">
            <i className="fa-solid fa-magnifying-glass" aria-hidden="true" />
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search name, phone, message" aria-label="Search chats" />
          </div>
          {widgetCount > 1 && (
            <select value={widgetId} onChange={e => setWidget(Number(e.target.value))} aria-label="Widget">
              <option value={0}>All widgets</option>
              {widgets.map(w => <option key={w.wId} value={w.wId}>{w.label}</option>)}
            </select>
          )}
        </div>

        {convs === null ? (
          <div className="lci-empty"><span className="wwg-spin" /> Loading chats…</div>
        ) : list.length === 0 ? (
          <div className="lci-empty">
            <i className="fa-regular fa-comments" aria-hidden="true" />
            <b>{q ? 'No chats match your search' : status === 'closed' ? 'No resolved chats yet' : status === 'human' ? 'Nothing waiting for you' : 'No chats yet'}</b>
            {status === 'human' && !q && <span>Chats the AI hands to a person show up here.</span>}
            {!q && (status === 'open' || status === 'all') && <span>When a visitor writes on your website, the chat appears here{presence?.online ? '' : ' — switch to Online so visitors see you’re around'}.</span>}
          </div>
        ) : (
          <ul className="lci-items">
            {list.map(c => {
              const name = displayName(c);
              return (
                <li key={c.id}>
                  <button type="button" className={`lci-item ${c.id === selId ? 'is-active' : ''} ${c.unread > 0 ? 'is-unread' : ''}`} onClick={() => setSelId(c.id)}>
                    <span className="lci-av" style={{ background: colorFor(name) }}>
                      {name[0]?.toUpperCase()}
                      {c.onPage && <i className="lci-live" title="On the website now" />}
                    </span>
                    <span className="lci-meta">
                      <span className="lci-top">
                        <b>{name}</b>
                        <em>{ago(c.lastMsgAt)}</em>
                      </span>
                      <span className="lci-bottom">
                        <span className={`lci-preview ${c.typing ? 'is-typing' : ''}`}>{c.typing ? 'typing…' : c.lastMsg}</span>
                        {c.unread > 0 && <span className="lci-unread">{c.unread > 9 ? '9+' : c.unread}</span>}
                      </span>
                      {(widgetCount > 1 || c.status === 'closed' || c.blocked || c.needsHuman || c.rating > 0 || (c.aiReplies > 0 && !c.aiPaused)) && (
                        <span className="lci-tags">
                          {c.needsHuman && c.status === 'open' && <span className="is-need">Needs you</span>}
                          {!c.needsHuman && c.aiReplies > 0 && !c.aiPaused && c.status === 'open' && <span className="is-ai">AI handling</span>}
                          {widgetCount > 1 && <span><i style={{ background: c.color }} />{c.widget}</span>}
                          {c.blocked ? <span className="is-bad">Blocked</span> : c.status === 'closed' && <span className="is-done"><i className="fa-solid fa-check" />Resolved</span>}
                          {c.rating > 0 && <span className="is-star" title={c.ratingNote || `Rated ${c.rating}/5`}><i className="fa-solid fa-star" />{c.rating}</span>}
                        </span>
                      )}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </aside>

      {!(isMobile && selId) && thread}
      {isMobile && selId && createPortal(<div className="wwg-vars lci-mobile">{thread}</div>, document.body)}
    </div>
  );
}
